import { useState, useEffect } from "react";
import "../css/signup.css";
import { FormProvider } from "react-hook-form";
import { useZodForm } from "../lib/form-utils";
import { signupSchema, SignupFormData } from "../schemas/signup-schema";
import { FormTextField } from "./forms/FormTextField";
import { useB2CPolicies } from "../hooks/useB2CPolicies";
import { useLocation } from "react-router";

const Signup: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState("");
  const [planError, setPlanError] = useState("");
  const [planId, setPlanId] = useState("");
  const [token, setToken] = useState("");

  // Use B2C policies hook for signup methods
  const { azureSignup, googleSignup } = useB2CPolicies();

  const methods = useZodForm({
    schema: signupSchema,
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planIdParam = params.get("planid");
    const tokenParam = params.get("token");

    if (planIdParam) {
      setPlanId(planIdParam);
      setToken(encodeURIComponent(tokenParam || ""));
    }
  }, [location.search]);

  const skypointLogin = () => {
    window.location.href = window.location.href.replace("signup", "skypointlogin");
  };

  const demo = () => {
    window.open("https://skypointcloud.com/demo/", "_blank");
  };

  const handleSubmit = async (data: SignupFormData) => {
    const email = data.email;

    if (!planId) {
      setPlanError("Invalid request please select ");
    } else {
      setError("");
      window.open(
        window.location.href.replace("signup", "skypointsignup") + "&email=" + email,
        "_self",
      );
    }
  };

  const renderForm = () => {
    return (
      <FormProvider {...methods}>
        <form
          id="createuser"
          onSubmit={methods.handleSubmit(handleSubmit)}
          className="signup-form"
        >
          <FormTextField
            name="email"
            type="email"
            className="input-field"
            placeholder="Enter email"
            required
          />
          <div className="block">
            <button
              type="submit"
              className="primary-btn"
            >
              Sign Up
            </button>
          </div>
        </form>
      </FormProvider>
    );
  };

  return (
    <div className="skypoint-signup-container">
      <div className="signup-left">
        <div className="logo-container">
          <img
            className="logo"
            src="https://sastudiob2cdevwu2.blob.core.windows.net/sicdpb2c/Images/Group%20137.png"
            alt="Skypoint logo"
          />
        </div>

        <div className="signup-box">
          <h2 className="welcome">Welcome back!</h2>
          <p>Create your account</p>
          <p
            id="signup-error"
            className="text-fg-danger page-error signup-error-plan userexists-error"
            aria-hidden="true"
            style={error ? { display: "block" } : { display: "none" }}
          >
            {error}
          </p>
          {planError !== "" && (
            <p
              id="signup-error"
              className="text-fg-danger page-error signup-error-plan"
              aria-hidden="true"
              style={planError === "" ? { display: "none" } : { display: "block" }}
            >
              <p className="planerror">
                Invalid request please select{" "}
                <a href="https://skypointcloud.com/pricing/"> Subscription plan.</a>{" "}
              </p>
            </p>
          )}
          {renderForm()}

          <div className="divider-line"></div>
          <div className="divider-or">Or</div>

          <button
            type="button"
            id="AzureADLoginsignup"
            className="provider-btn ms"
            onClick={() => azureSignup(planId, token)}
          >
            <img
              src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/microsoft_signup_logo.png"
              alt="ms"
            />
            Sign up with Microsoft
          </button>

          <button
            type="button"
            id="GoogleADLoginsignup"
            className="provider-btn google"
            onClick={() => googleSignup(planId, token)}
          >
            <img
              src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/google_signup_logo.png"
              alt="google"
            />
            Sign up with Google
          </button>

          <button
            type="button"
            id="SkyPointsignup"
            className="provider-btn email"
            onClick={skypointLogin}
          >
            <img
              src="https://sicdpstorage.blob.core.windows.net/sicdpb2c/assets/images/skypoint-sign-up-icon.png"
              alt="email"
            />
            Sign up with Email and Password
          </button>

          <div className="signup-footer">
            <p>
              Already have an account? <a href={window.location.origin}>Sign in</a>
            </p>

            <p className="signup-disclaimer">
              With signing in, you accept our{" "}
              <a href="https://skypointcloud.com/terms/">terms and conditions</a> and confirm that
              you have taken note of our <a href="https://privacy.skypoint.ai/">privacy policy</a>.
            </p>

            <div className="divider-line"></div>

            <p className="small-text">
              Questions? <a href="https://skypointcloud.com/customer-support/">Contact Support</a>
            </p>

            <p className="copies">
              Copyright © 2025{" "}
              <a
                href="https://skypoint.ai/"
                target="_blank"
                rel="noreferrer"
              >
                SkyPoint
              </a>
              . All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <div className="signup-right">
        <div className="hero-text">
          <h1>
            AI Agents for <span className="highlight">healthcare</span>
          </h1>
          <p>
            Schedule a complimentary demo with our specialists to discover how Skypoint's AI
            Platform seamlessly integrates your data landscape, unleashing the capabilities of
            generative AI to deliver precise, industry-tailored insights throughout your
            organization.
          </p>

          <button
            className="demo-btn"
            onClick={demo}
          >
            Request Demo
            <img
              src="https://sastudiob2cdevwu2.blob.core.windows.net/sicdpb2c/Images/rqst-demo.png"
              alt="arrow"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Signup;
